import uz from './uz.json' with { type: 'json' };
import ru from './ru.json' with { type: 'json' };
import en from './en.json' with { type: 'json' };

/**
 * Lug'atlar **tekis** (`"nav.warehouses"`), ichma-ich emas: kalitni grep bilan
 * topish oson va `uz` bilan solishtirish bitta `Object.keys` ga tushadi.
 * `uz` — manba til; qolgan ikkitasi undan kalit-ba-kalit ko'chiriladi va buni
 * `i18n.test.ts` tekshirib turadi.
 */
export type Dictionary = Record<string, string>;

export type Locale = 'uz' | 'ru' | 'en';

export const DICTIONARIES: Record<Locale, Dictionary> = {
  uz: uz as Dictionary,
  ru: ru as Dictionary,
  en: en as Dictionary,
};

/**
 * Manba til — ham lug'atlarning asosi, ham ilovaning BOSHLANG'ICH tili.
 * Lug'atlar shundan ko'chiriladi va kalitlar shu fayl bo'yicha tekshiriladi.
 */
export const DEFAULT_LOCALE: Locale = 'uz';

/** Tanlov shu kalit ostida saqlanadi — brauzer yopilsa ham qoladi. */
export const LOCALE_STORAGE_KEY = 'eombor:locale';

const LOCALES: readonly Locale[] = ['uz', 'ru', 'en'];

const isLocale = (value: string | null): value is Locale =>
  value !== null && (LOCALES as readonly string[]).includes(value);

/**
 * Boshlang'ich til: saqlangan tanlov → `uz`.
 *
 * `navigator.language` dan aniqlash ATAYLAB olib tashlandi. Bu O'zbekiston
 * temir yo'llari tizimi: xodimning brauzeri ko'pincha inglizcha yoki ruscha
 * o'rnatilgan bo'ladi, ishlash tili esa baribir o'zbekcha. Brauzer tiliga
 * ergashish ilovani birinchi ochilishda begona tilda ko'rsatardi.
 *
 * Foydalanuvchining O'Z tanlovi esa doim hurmat qilinadi — u saqlanadi va
 * shu yerda birinchi bo'lib o'qiladi. `localStorage` taqiqlangan bo'lsa
 * (maxfiy oyna, korporativ siyosat) jim o'tamiz: til tanlash ilovani
 * to'xtatadigan narsa emas.
 */
const detectLocale = (): Locale => {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Saqlash o'qilmadi — manba tilda qolamiz.
  }
  return DEFAULT_LOCALE;
};

let currentLocale: Locale = detectLocale();

/** Til o'zgarishini kuzatuvchilar — `LocaleProvider` shular orqali qayta chizadi. */
const listeners = new Set<(locale: Locale) => void>();

export const getLocale = (): Locale => currentLocale;

/**
 * Tilni almashtiradi va tanlovni saqlaydi. `t()` modul darajasidagi shu
 * qiymatdan o'qigani uchun almashtirishdan keyin daraxt qayta chizilishi
 * SHART — buni `LocaleProvider` obuna orqali bajaradi.
 */
export const setLocale = (locale: Locale): void => {
  if (locale === currentLocale) return;
  currentLocale = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Saqlanmadi — tanlov faqat shu sessiyada yashaydi, lekin ilova ishlayveradi.
  }
  for (const listener of listeners) listener(locale);
};

/** Obuna. Qaytgan funksiya obunani uzadi. */
export const subscribeLocale = (listener: (locale: Locale) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Kalitni matnga aylantiradi. Kalit topilmasa — **kalitning o'zi** qaytadi:
 * ekranda bo'sh joy emas, aniq `nav.warehouses` ko'rinadi va nima yetishmayotgani
 * darhol bilinadi. `dev` da yana konsolga ogohlantirish yoziladi.
 */
export const translate = (
  dict: Dictionary,
  key: string,
  params: Record<string, string | number> = {},
): string => {
  const template = dict[key];
  if (template === undefined) {
    if (import.meta.env?.DEV === true) console.warn(`[i18n] kalit topilmadi: ${key}`);
    return key;
  }
  return template.replace(/\{(\w+)\}/g, (_m, name: string) => String(params[name] ?? `{${name}}`));
};

/** Ilova bo'ylab ishlatiladigan qisqa ko'rinish — joriy tildan o'qiydi. */
export const t = (key: string, params?: Record<string, string | number>): string =>
  translate(DICTIONARIES[currentLocale], key, params);
